import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Clubhouse } from '@/lib/reference/clubhouse';
import { Crest } from '@/components/shared/Crest';

export function ClubhouseCard({ club }: { club: Clubhouse }) {
  const [expanded, setExpanded] = useState(false);
  const [venueId, setVenueId] = useState<string | null>(null);
  const venue = venueId ? club.venues.find((v) => v.id === venueId) : null;
  const phoneDigits = club.phone.replace(/[^0-9]/g, '');

  return (
    <div className="mb-3.5">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="relative flex w-full items-center gap-3 overflow-hidden rounded-2xl bg-navy p-4 text-left text-cream shadow-bt-md"
      >
        <div className="pointer-events-none absolute -right-3.5 -top-3.5 opacity-[0.07]">
          <Crest size={100} variant="dark" />
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold text-xl text-navy">🍴</div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg font-bold">{club.name}</div>
          <div className="truncate text-[11px] text-white/65">{club.address}</div>
        </div>
        <a
          href={`tel:${phoneDigits}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 rounded-full bg-gold px-3 py-2 text-xs font-bold text-navy no-underline"
        >
          📞 Call
        </a>
      </button>

      {expanded && !venue && (
        <div className="mt-2 space-y-1.5">
          {club.venues.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVenueId(v.id)}
              className="flex w-full items-center gap-3 rounded-xl border border-line bg-paper p-3.5 text-left shadow-bt"
            >
              <div className="min-w-0 flex-1">
                <div className="font-serif text-[15px] font-bold text-navy">{v.name}</div>
                <div className="mt-0.5 text-[11px] font-semibold text-gold">{v.kind}</div>
                <div className="mt-0.5 truncate text-[11px] text-slate-400">{v.hours}</div>
              </div>
              <ChevronRight size={18} className="text-slate-400" />
            </button>
          ))}
          <p className="px-1 text-[10px] leading-relaxed text-slate-400">{club.note}</p>
        </div>
      )}

      {expanded && venue && (
        <div className="mt-2 rounded-xl border border-line bg-paper p-4 shadow-bt">
          <button type="button" onClick={() => setVenueId(null)} className="mb-3 flex items-center gap-1 text-sm text-slate-500">
            <ChevronLeft size={16} /> All {club.name.split(' ')[0]} venues
          </button>
          <div className="text-[11px] font-bold uppercase tracking-wide text-gold">{venue.kind}</div>
          <div className="font-serif text-2xl font-bold text-navy">{venue.name}</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{venue.about}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-md bg-cream px-2.5 py-1 text-[11px] text-slate-600">🕐 {venue.hours}</span>
            <span className="rounded-md bg-cream px-2.5 py-1 text-[11px] text-slate-600">👔 {venue.dress}</span>
          </div>
          <a
            href={`tel:${venue.phone.replace(/[^0-9]/g, '')}`}
            className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-navy py-3.5 text-sm font-bold text-cream no-underline"
          >
            📞 Call to Order · {venue.phone}
          </a>
          <div className="mt-4 space-y-3">
            {(() => {
              const categories = [...new Set(venue.items.map((i) => i.category).filter(Boolean))] as string[];
              const groups = categories.length ? categories : ['Menu'];
              return groups.map((cat) => (
                <div key={cat}>
                  {categories.length > 0 && (
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-px flex-1 bg-line" />
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gold-deep">{cat}</div>
                      <div className="h-px flex-1 bg-line" />
                    </div>
                  )}
                  {venue.items.filter((item) => !categories.length || item.category === cat).map((item, i) => (
                    <div key={`${item.name}-${i}`} className="mb-2 rounded-lg border border-line bg-cream/50 px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-ink">{item.name}</div>
                          {item.note && <div className="text-[11px] text-mute">{item.note}</div>}
                        </div>
                        <div className="shrink-0 font-mono text-sm font-semibold text-navy">
                          {typeof item.price === 'number' ? `$${item.price}` : item.price}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
